/**
 * Social Feed Service - Fetches recent YouTube uploads and Instagram posts
 * from the backend, which reads credentials saved in Platform Integrations.
 */

import { CacheManager } from '../utils/cacheManager';
import { API_ENDPOINTS, BUSINESS_ID, buildQueryString, resolveShopBusinessId } from '../config/api';

const EMPTY_YOUTUBE = { videos: [], channelUrl: null };
const EMPTY_INSTAGRAM = { posts: [], profileUrl: null };

class SocialFeedService {
  constructor() {
    this.youtubeCache = new CacheManager(5 * 60 * 1000);
    this.instagramCache = new CacheManager(5 * 60 * 1000);
  }

  /** @returns {Promise<{videos: Array, channelUrl: string|null}>} */
  async getYoutubeFeed(options = {}) {
    if (!options.forceRefresh) {
      const cached = this.youtubeCache.get();
      if (cached) return cached;
    }

    try {
      const businessId = (await resolveShopBusinessId()) || BUSINESS_ID;
      if (!businessId) return EMPTY_YOUTUBE;

      const url = `${API_ENDPOINTS.socialFeed.youtube}?${buildQueryString({ businessId })}`;
      const response = await fetch(url);
      if (!response.ok) return EMPTY_YOUTUBE;

      const data = await response.json();
      const result = {
        videos: Array.isArray(data?.videos) ? data.videos : [],
        channelUrl: data?.channelUrl || null,
      };
      this.youtubeCache.set(result);
      return result;
    } catch (error) {
      console.warn('⚠️ [SOCIAL FEED] Failed to load YouTube videos:', error);
      return EMPTY_YOUTUBE;
    }
  }

  /** @returns {Promise<{posts: Array, profileUrl: string|null}>} */
  async getInstagramFeed(options = {}) {
    if (!options.forceRefresh) {
      const cached = this.instagramCache.get();
      if (cached) return cached;
    }

    try {
      const businessId = (await resolveShopBusinessId()) || BUSINESS_ID;
      if (!businessId) return EMPTY_INSTAGRAM;

      const url = `${API_ENDPOINTS.socialFeed.instagram}?${buildQueryString({ businessId })}`;
      const response = await fetch(url);
      if (!response.ok) return EMPTY_INSTAGRAM;

      const data = await response.json();
      const result = {
        posts: Array.isArray(data?.posts) ? data.posts : [],
        profileUrl: data?.profileUrl || null,
      };
      this.instagramCache.set(result);
      return result;
    } catch (error) {
      console.warn('⚠️ [SOCIAL FEED] Failed to load Instagram posts:', error);
      return EMPTY_INSTAGRAM;
    }
  }
}

const socialFeedService = new SocialFeedService();
export default socialFeedService;
